import React from 'react';
import { useTranslation } from 'react-i18next';
import { TextField } from '@material-ui/core';
import { useField } from 'formik';
import { FormikProps, FormikValues, useFormikContext } from 'formik';

import useStudentsFormKey from '../../../hooks/use-students-form-key';
import SecretCodeSelect from 'components/@common/SecretCodeSelect';
import { DisplayDirection } from 'components/@common/SecretCodeSelect/types';
import { StudentLoginType } from 'types/schema-generated-types';
import { StudentFieldProps } from '../../../types';

export const StudentSecretCodeField: React.FC<StudentFieldProps> = ({ index, touched }) => {
  const { t } = useTranslation('AddStudentsStep');
  const [secretCodeAnchorEl, setSecretCodeAnchorEl] = React.useState<null | HTMLElement>(null);
  const { values }: FormikProps<FormikValues> = useFormikContext();
  const studentsFormKey = useStudentsFormKey();
  const name = `${studentsFormKey}.${index}.secretCode`;
  const loginType = values.CREATE_CLASS?.secretCodeType;

  const handleSecretCodeClick = (event: React.MouseEvent<HTMLInputElement>): void => {
    setSecretCodeAnchorEl(event.currentTarget);
  };

  const [field, meta, helpers] = useField({ name });
  const { onChange, value, ...remainingField } = field;

  const handleSecretCodeClose = React.useCallback((): void => {
    helpers.setTouched(true);
    setSecretCodeAnchorEl(null);
  }, [setSecretCodeAnchorEl, helpers]);
  const handleSecretChange = (loginType: StudentLoginType, code: string): void => {
    onChange({
      target: { value: { code, loginType }, name }
    });
  };
  const getError = (): string => {
    if (typeof meta.error === 'string') {
      return meta.error;
    }
    return '';
  };
  const error = getError();

  return (
    <>
      <TextField
        {...field}
        key={`${field.name}_input`}
        fullWidth
        placeholder={t('Secret Code')}
        variant="outlined"
        size="small"
        value={value ? value.code : ''}
        error={Boolean((touched || meta.touched) && error)}
        helperText={(touched || meta.touched) && error}
        inputProps={{
          ...{
            'aria-label': `${t('Student')} ${index + 1} ${t('Secret Code')}`
          },
          readOnly: true,
          onClick: handleSecretCodeClick
        }}
        {...remainingField}
      />

      <SecretCodeSelect
        displayDirection={DisplayDirection.ROW}
        open={secretCodeAnchorEl !== null}
        anchorEl={secretCodeAnchorEl || undefined}
        loginType={value ? value.loginType : loginType || StudentLoginType.LETTER_CARDS}
        secretCode={value ? value.code : ''}
        handleClose={handleSecretCodeClose}
        handleConfirm={handleSecretChange}
      />
    </>
  );
};
